import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useUIStore } from '@/stores/uiStore';

export type RouteScope = 'today' | 'calendar' | 'inbox' | 'routines' | 'habits' | 'settings';

export interface BindingDescriptor {
  key: string; // KeyboardEvent.key (case-insensitive for letters)
  cmd?: boolean;
  shift?: boolean;
  description: string;
  ignoreInInput?: boolean; // default true
  scope?: RouteScope[]; // if set, only fires on matching routes
  display: string; // human-readable combo for the help dialog (e.g., "⌘K", "←")
}

const PATH_TO_SCOPE: Record<string, RouteScope> = {
  '/app': 'today',
  '/app/inbox': 'inbox',
  '/app/routines': 'routines',
  '/app/habits': 'habits',
  '/app/calendar': 'calendar',
  '/app/settings': 'settings',
};

/**
 * The static binding map. Imported by ShortcutsHelpDialog to render the help UI,
 * so adding/changing a binding here automatically updates the help.
 */
export const BINDINGS: BindingDescriptor[] = [
  { key: 'k', cmd: true, description: 'Open command palette', ignoreInInput: false, display: '⌘K' },
  { key: 'n', description: 'New task (focus quick capture)', display: 'N' },
  { key: 'i', description: 'Go to Inbox', display: 'I' },
  { key: 't', description: 'Go to Today (and reset date)', display: 'T' },
  { key: 'ArrowLeft', description: 'Previous day', scope: ['today', 'calendar'], display: '←' },
  { key: 'ArrowRight', description: 'Next day', scope: ['today', 'calendar'], display: '→' },
  { key: 'Escape', description: 'Close any open overlay', ignoreInInput: false, display: 'Esc' },
  { key: '?', shift: true, description: 'Show keyboard shortcuts', display: '?' },
];

function isInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

function matches(b: BindingDescriptor, e: KeyboardEvent): boolean {
  if (b.cmd && !(e.metaKey || e.ctrlKey)) return false;
  if (!b.cmd && (e.metaKey || e.ctrlKey)) return false;
  if (b.shift && !e.shiftKey) return false;
  if (b.key.length === 1) {
    return e.key.toLowerCase() === b.key.toLowerCase();
  }
  return e.key === b.key;
}

export function useKeyboardShortcuts(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const setCommandPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const toggleCommandPalette = useUIStore((s) => s.toggleCommandPalette);
  const setShortcutsHelpOpen = useUIStore((s) => s.setShortcutsHelpOpen);
  const setLastViewedDate = useUIStore((s) => s.setLastViewedDate);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const currentScope = PATH_TO_SCOPE[location.pathname];
      for (const b of BINDINGS) {
        if (!matches(b, e)) continue;
        const ignoresInput = b.ignoreInInput ?? true;
        if (ignoresInput && isInputTarget(e.target)) continue;
        if (b.scope && (!currentScope || !b.scope.includes(currentScope))) continue;

        e.preventDefault();
        switch (b.key) {
          case 'k':
            toggleCommandPalette();
            break;
          case 'n': {
            const el = document.querySelector<HTMLInputElement>('[data-quick-capture-input]');
            el?.focus();
            break;
          }
          case 'i':
            void navigate('/app/inbox');
            break;
          case 't':
            setLastViewedDate(new Date());
            void navigate('/app');
            break;
          case 'ArrowLeft':
          case 'ArrowRight': {
            const cur = useUIStore.getState().lastViewedDate;
            const delta = b.key === 'ArrowRight' ? 1 : -1;
            const next = new Date(cur);
            next.setDate(next.getDate() + delta);
            setLastViewedDate(next);
            break;
          }
          case 'Escape':
            setCommandPaletteOpen(false);
            setShortcutsHelpOpen(false);
            break;
          case '?':
            setShortcutsHelpOpen(true);
            break;
        }
        return;
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    navigate,
    location.pathname,
    toggleCommandPalette,
    setCommandPaletteOpen,
    setShortcutsHelpOpen,
    setLastViewedDate,
  ]);
}
